/**
 * ImportMappingModal - Modal pour mapper les lignes de facture aux produits
 */
import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { formatPrice } from '../../utils/pos.utils';
import { logger } from '../../utils/logger';

interface ImportedLine {
  id: string;
  line_number: number;
  description: string;
  quantity: number;
  unit_price: number;
  vat_rate: number;
  matched_product_id?: string;
  matched_product_name?: string;
  matched_product_stock_total?: number;
  matched_product_stock_display?: number;
  match_status: 'pending' | 'matched' | 'confirmed' | 'skipped';
  match_confidence?: number;
  // 'history' = déduit des choix faits pour ce libellé sur les factures précédentes
  match_type?: 'name_similarity' | 'manual' | 'history';
  suggested_products?: Array<{
    product_id: string;
    product_name: string;
    product_code?: string;
    confidence: number;
    match_type?: 'name_similarity' | 'manual' | 'history';
    deja_choisi?: number;
    preselection_raison?: 'habitude' | 'stock';
    stock_total?: number;
    stock_display?: number;
  }>;
  stock_deducted: boolean;
}

interface ImportedInvoice {
  id: string;
  enfact_invoice_id: number;
  enfact_invoice_number?: string;
  import_status: 'pending_mapping' | 'pending_validation' | 'validated' | 'stock_deducted' | 'completed';
  enfact_customer_name?: string;
  enfact_total_excl_vat?: number;
  enfact_total_incl_vat?: number;
  enfact_date?: string;
  lines: ImportedLine[];
}

interface Product {
  id: string;
  name: string;
  code?: string;
}

interface Props {
  invoice: ImportedInvoice;
  tenantId: string;
  userId: string;
  onClose: () => void;
}

export default function ImportMappingModal({ invoice, tenantId, userId, onClose }: Props) {
  const [lines, setLines] = useState<ImportedLine[]>(invoice.lines);
  const [status, setStatus] = useState(invoice.import_status);
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [autoMatchTried, setAutoMatchTried] = useState(false);

  // Charger les produits pour la recherche
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await api.get(`/products?tenant_id=${tenantId}&active=true&limit=5000`);
        // Gérer différents formats de réponse API
        let productList: Product[] = [];
        if (Array.isArray(data)) {
          productList = data;
        } else if (data && typeof data === 'object') {
          if (Array.isArray(data.products)) {
            productList = data.products;
          } else if (Array.isArray(data.items)) {
            productList = data.items;
          } else if (Array.isArray(data.data)) {
            productList = data.data;
          }
        }
        setProducts(productList);
      } catch (err) {
        logger.error('[ImportMappingModal] Erreur chargement produits:', err);
        setProducts([]);
      }
    };
    loadProducts();
  }, [tenantId]);

  // Recharger les donnees de la facture
  const reloadInvoice = async () => {
    try {
      const data = await api.get(`/enfact/${tenantId}/import/${invoice.id}`);
      setLines(data.lines || []);
      setStatus(data.import_status);
    } catch (err) {
      logger.error('[ImportMappingModal] Erreur reload:', err);
    }
  };

  // Lancer le matching automatique
  const handleAutoMatch = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.post(`/enfact/${tenantId}/import/${invoice.id}/match-lines`);
      logger.log('[ImportMappingModal] Match result:', result);
      await reloadInvoice();
      const ignorees = result.ignored ? `, ${result.ignored} proposee(s) a ignorer comme d'habitude` : '';
      setSuccess(`${result.matched} ligne(s) proposee(s)${ignorees}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du matching');
    } finally {
      setLoading(false);
    }
  };

  // À l'ouverture, on relance le matching une fois s'il reste des lignes en attente
  // ou seulement proposées (jamais celles confirmées ou ignorées à la main) :
  // les propositions stockées à l'import ne tiennent pas compte des choix faits depuis
  // sur les autres factures (le matching apprend de l'historique).
  useEffect(() => {
    if (autoMatchTried || loading) return;
    const needsMatch =
      (status === 'pending_mapping' || status === 'pending_validation') &&
      lines.some((l) => l.match_status === 'pending' || l.match_status === 'matched');
    if (needsMatch) {
      setAutoMatchTried(true);
      handleAutoMatch();
    }
  }, [autoMatchTried, loading, status, lines]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mettre a jour le mapping d'une ligne
  const handleUpdateLine = async (lineId: string, productId: string | null, status: ImportedLine['match_status']) => {
    setLoading(true);
    setError(null);
    try {
      await api.put(`/enfact/${tenantId}/import/${invoice.id}/line/${lineId}`, {
        matched_product_id: productId,
        match_status: status,
      });
      await reloadInvoice();
      setEditingLineId(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la mise a jour');
    } finally {
      setLoading(false);
    }
  };

  // Confirmer d'un geste toutes les lignes proposées
  const handleConfirmMatched = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.post(`/enfact/${tenantId}/import/${invoice.id}/confirm-matched`);
      await reloadInvoice();
      setSuccess(`${result.confirmed} ligne(s) confirmee(s)`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la confirmation');
    } finally {
      setLoading(false);
    }
  };

  // Valider l'import
  const handleValidate = async () => {
    if (!userId) {
      setError('Session expiree : reconnectez-vous pour valider la facture.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await api.post(`/enfact/${tenantId}/import/${invoice.id}/validate`, { userId });
      await reloadInvoice();
      setSuccess('Facture validee !');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la validation');
    } finally {
      setLoading(false);
    }
  };

  // Deduire le stock
  const handleDeductStock = async () => {
    if (!userId) {
      setError('Session expiree : reconnectez-vous pour deduire le stock.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await api.post(`/enfact/${tenantId}/import/${invoice.id}/deduct-stock`, { userId });
      logger.log('[ImportMappingModal] Deduction result:', result);
      await reloadInvoice();
      const allResults: any[] = result.results || [];
      const succeeded = allResults.filter((r) => r.success);
      const failed = allResults.filter((r) => !r.success);

      if (allResults.length === 0) {
        setError(
          "Aucune ligne a deduire : confirmez au moins une ligne avec un produit matche avant de deduire le stock.",
        );
      } else if (failed.length > 0) {
        const details = failed
          .map((r) => {
            const line = lines.find((l) => l.id === r.lineId);
            const label = line?.description || r.productId || r.lineId;
            return `• ${label} : ${r.error || 'echec'}`;
          })
          .join('\n');
        setError(
          `Stock deduit pour ${succeeded.length}/${allResults.length} ligne(s). Echecs :\n${details}`,
        );
      } else {
        // Déduction forcée : le stock s'arrête à 0 quand la facture dépasse le disponible.
        // Le reliquat doit être VISIBLE, sinon on recrée l'angle mort qu'on vient de fermer.
        const partielles = succeeded.filter((r) => r.shortfall && r.shortfall > 0);
        if (partielles.length > 0) {
          const details = partielles
            .map((r) => {
              const line = lines.find((l) => l.id === r.lineId);
              const label = line?.description || r.productId || r.lineId;
              return `• ${label} : ${r.shortfall} non impute (stock ramene a 0)`;
            })
            .join('\n');
          setSuccess(
            `Stock deduit pour ${succeeded.length} ligne(s). Stock insuffisant sur ` +
            `${partielles.length} ligne(s), ramenees a 0 :\n${details}`,
          );
        } else {
          setSuccess(`Stock deduit pour ${succeeded.length} ligne(s)`);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la deduction');
    } finally {
      setLoading(false);
    }
  };

  // Marquer comme traitee sans deduire le stock (produits non suivis)
  const handleMarkProcessed = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.post(`/enfact/${tenantId}/import/${invoice.id}/mark-processed`, { userId });
      await reloadInvoice();
      setSuccess('Facture marquee comme traitee (sans deduction de stock)');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du marquage');
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les produits pour la recherche
  const filteredProducts = (products || []).filter(
    (p) =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.code && p.code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Obtenir la couleur selon le statut de matching
  const getMatchColor = (line: ImportedLine) => {
    if (line.match_status === 'confirmed') return 'bg-green-50 border-green-200';
    if (line.match_status === 'matched') return 'bg-blue-50 border-blue-200';
    if (line.match_status === 'skipped' || proposeeAIgnorer(line)) return 'bg-gray-50 border-gray-200';
    if (line.match_confidence && line.match_confidence >= 85) return 'bg-green-50 border-green-200';
    if (line.match_confidence && line.match_confidence >= 50) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  // Verifier si toutes les lignes sont traitees
  const allLinesProcessed = lines.every(
    (l) => l.match_status === 'confirmed' || l.match_status === 'skipped'
  );

  // Verifier si on peut deduire le stock
  const canDeductStock = status === 'validated';

  // Lignes proposées (présélectionnées) qui attendent une confirmation
  // Ligne que l'historique propose d'IGNORER (toujours ignorée jusqu'ici, jamais
  // rattachée à un produit) : en attente, 'history', sans produit
  const proposeeAIgnorer = (l: ImportedLine) =>
    l.match_status === 'pending' && l.match_type === 'history' && !l.matched_product_id;
  const lignesProposees = lines.filter(
    (l) =>
      !l.stock_deducted &&
      ((l.match_status === 'matched' && l.matched_product_id) || proposeeAIgnorer(l)),
  ).length;

  // Libellé de l'origine d'une présélection
  const origineProposition = (line: ImportedLine): string | null => {
    if (line.match_type !== 'history') return null;
    const s = line.suggested_products?.find((sg) => sg.product_id === line.matched_product_id);
    if (s?.preselection_raison === 'stock') return "l'habituel n'a plus de stock";
    if (s?.deja_choisi) return `choisi ${s.deja_choisi} fois pour ce libelle`;
    return 'choix habituel';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Facture {invoice.enfact_invoice_number || `#${invoice.enfact_invoice_id}`}
            </h2>
            <p className="text-sm text-gray-600">
              {invoice.enfact_customer_name} - {invoice.enfact_date ? new Date(invoice.enfact_date).toLocaleDateString('fr-BE') : ''}
              {invoice.enfact_total_incl_vat && ` - ${formatPrice(invoice.enfact_total_incl_vat)}`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
            &times;
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm whitespace-pre-line">
            {error}
          </div>
        )}
        {success && (
          <div className="mx-6 mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm whitespace-pre-line">
            {success}
          </div>
        )}

        {/* Actions */}
        <div className="px-6 py-3 border-b border-gray-200 flex items-center gap-3">
          {status === 'pending_mapping' && (
            <button
              onClick={handleAutoMatch}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {loading ? 'Matching...' : 'Matching automatique'}
            </button>
          )}
          {(status === 'pending_mapping' || status === 'pending_validation') && lignesProposees > 0 && (
            <button
              onClick={handleConfirmMatched}
              disabled={loading}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium"
            >
              Confirmer les {lignesProposees} proposition{lignesProposees > 1 ? 's' : ''}
            </button>
          )}
          {(status === 'pending_mapping' || status === 'pending_validation') && allLinesProcessed && (
            <button
              onClick={handleValidate}
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium"
            >
              Valider
            </button>
          )}
          {canDeductStock && (
            <button
              onClick={handleDeductStock}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
            >
              Deduire le stock
            </button>
          )}
          {canDeductStock && (
            <button
              onClick={handleMarkProcessed}
              disabled={loading}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm font-medium"
              title="Pour les factures dont les produits ne sont pas suivis en stock"
            >
              Marquer comme traitee
            </button>
          )}
          <span className="ml-auto text-sm text-gray-500">
            Statut: <span className="font-medium">{status}</span>
          </span>
        </div>

        {/* Liste des lignes */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-3">
            {lines.map((line) => (
              <div
                key={line.id}
                className={`border rounded-lg p-4 ${getMatchColor(line)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-sm">#{line.line_number}</span>
                      <span className="font-medium">{line.description}</span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Qte: {line.quantity} | Prix HT: {formatPrice(line.unit_price * 100)} | TVA: {line.vat_rate}%
                    </div>

                    {proposeeAIgnorer(line) && (
                      <div className="mt-2 text-sm text-gray-600">
                        Propose : <span className="font-medium">ignorer</span>
                        <span className="ml-1 text-xs text-gray-500">(ignoree les dernieres fois, jamais liee a un produit)</span>
                      </div>
                    )}

                    {/* Produit matche */}
                    {line.matched_product_name && (
                      <div className="mt-2 text-sm">
                        <span className="text-gray-500">Produit: </span>
                        <span className="font-medium text-green-700">{line.matched_product_name}</span>
                        {origineProposition(line) ? (
                          <span className="ml-2 text-emerald-700 text-xs">({origineProposition(line)})</span>
                        ) : (
                          line.match_confidence && (
                            <span className="ml-2 text-gray-400">({line.match_confidence}%)</span>
                          )
                        )}
                        {typeof line.matched_product_stock_total === 'number' && (
                          <span
                            className={`ml-2 text-xs font-medium ${
                              line.matched_product_stock_total > 0 ? 'text-gray-500' : 'text-red-600'
                            }`}
                          >
                            stock {line.matched_product_stock_total}
                            {line.matched_product_stock_display !== undefined &&
                            line.matched_product_stock_total !== line.matched_product_stock_display
                              ? ` (rayon ${line.matched_product_stock_display})`
                              : ''}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Suggestions */}
                    {editingLineId === line.id && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                        <input
                          type="text"
                          placeholder="Rechercher un produit..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"
                        />
                        <div className="max-h-40 overflow-auto space-y-1">
                          {filteredProducts.slice(0, 10).map((product) => (
                            <button
                              key={product.id}
                              onClick={() => handleUpdateLine(line.id, product.id, 'confirmed')}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded text-sm"
                            >
                              {product.name}
                              {product.code && <span className="text-gray-400 ml-2">({product.code})</span>}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Propositions — toujours offertes tant que la ligne n'est pas confirmée/ignorée */}
                    {!editingLineId && line.suggested_products && line.suggested_products.length > 0 && line.match_status !== 'confirmed' && line.match_status !== 'skipped' && (
                      <div className="mt-2">
                        <div className="text-xs text-gray-500 mb-1">Propositions :</div>
                        <div className="flex flex-wrap gap-2">
                          {line.suggested_products.slice(0, 5).map((suggestion) => (
                            <button
                              key={suggestion.product_id}
                              onClick={() => handleUpdateLine(line.id, suggestion.product_id, 'confirmed')}
                              className="px-2 py-1 bg-white border border-gray-300 rounded text-xs hover:bg-blue-50"
                            >
                              {suggestion.product_name}{' '}
                              {suggestion.deja_choisi ? (
                                <span className="text-emerald-700">· deja choisi {suggestion.deja_choisi}×</span>
                              ) : (
                                `(${suggestion.confidence}%)`
                              )}
                              {typeof suggestion.stock_total === 'number' && (
                                <span
                                  className={
                                    suggestion.stock_total > 0 ? 'text-emerald-600' : 'text-red-600'
                                  }
                                >
                                  {' '}
                                  · stock {suggestion.stock_total}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    {line.match_status === 'confirmed' && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Confirme</span>
                    )}
                    {line.match_status === 'skipped' && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Ignore</span>
                    )}
                    {line.stock_deducted && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Stock deduit</span>
                    )}

                    {/* Revenir en arriere sur une ligne ignoree — possible en tout temps tant que
                        la facture n'est pas encore deduite (remet la ligne en attente + ses propositions). */}
                    {status !== 'stock_deducted' && !line.stock_deducted && line.match_status === 'skipped' && (
                      <button
                        onClick={() => handleUpdateLine(line.id, line.matched_product_id ?? null, 'pending')}
                        className="px-3 py-1 bg-amber-100 text-amber-700 rounded text-sm hover:bg-amber-200"
                      >
                        Réactiver
                      </button>
                    )}

                    {status !== 'stock_deducted' && line.match_status !== 'skipped' && (
                      <>
                        {editingLineId === line.id ? (
                          <button
                            onClick={() => setEditingLineId(null)}
                            className="px-3 py-1 text-gray-600 text-sm"
                          >
                            Annuler
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingLineId(line.id);
                              setSearchTerm('');
                            }}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                          >
                            Changer
                          </button>
                        )}
                        {line.match_status !== 'confirmed' && line.matched_product_id && (
                          <button
                            onClick={() => handleUpdateLine(line.id, line.matched_product_id!, 'confirmed')}
                            className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200"
                          >
                            Confirmer
                          </button>
                        )}
                        <button
                          onClick={() => handleUpdateLine(line.id, null, 'skipped')}
                          className="px-3 py-1 bg-gray-100 text-gray-600 rounded text-sm hover:bg-gray-200"
                        >
                          Ignorer
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
